/******************************************************************************
 *** BgtSpcEvdncDialog ** 특별회계기금 지출증빙내역서 모달
*******************************************************************************/
import AppContext from "@/js/common/AppContext";
import DatePickerBox from "@/js/common/ui/input/DatePickerBox";
import NumberTextBox from "@/js/common/ui/input/NumberTextBox";
import TextBox from "@/js/common/ui/input/TextBox";
import BudgetFile from "../../budgetFileBox/BudgetFile";
import Button from "@/js/common/ui/Button";
import React from "react";
import { budgetFilesProps } from "@/js/common/types/types";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";

interface Props {
    spcAcntgExpndDocId     : string; // 지출결의서 문서 ID
    spcAcntgExpndEvdncId   : string; // 지출증빙내역서 ID
    setSpcAcntgExpndEvdncId: Function; // 지출증빙내역서 ID Setting
    acntgYear              : string;   // 회계연도
    setSpcEvdncModalOpen   : Function;
    setSpcEvdncDelYn       : Function; // 지출증빙내역서 삭제 여부
    availAmount            : string;   // 집행가능금액
    sumUseAmt              : string;   // 지출증빙내역서 사용금액 합계
    expndRsdocSeCd        ?: string;   // 지출결의서 구분 코드 (03 = 여입)
    isEvdncUse             : boolean;  // 증빙첨부 사용여부
    setCallTime            : Function;
}
const BgtSpcEvdncDialog = ( {spcAcntgExpndDocId, spcAcntgExpndEvdncId, setSpcAcntgExpndEvdncId, acntgYear, setSpcEvdncModalOpen, setSpcEvdncDelYn, availAmount, sumUseAmt, setCallTime, isEvdncUse, expndRsdocSeCd}: Props) => {
    const [useDt    , setUseDt    ] = React.useState<string>(""); // 사용일자
    const [usePrpsCn, setUsePrpsCn] = React.useState<string>(""); // 사용목적내용
    const [useCn    , setUseCn    ] = React.useState<string>(""); // 사용내용
    const [useAmt   , setUseAmt   ] = React.useState<string>(""); // 사용금액
    const [etcCn    , setEtcCn    ] = React.useState<string>(""); // 기타내용
    const [rmrkCn   , setRmrkCn   ] = React.useState<string>(""); // 비고내용

    const [spcEvdncFiles     , setSpcEvdncFiles    ] = React.useState<File[]>([]); // 신규 첨부(저장할 파일)
    const [savedSpcEvdncFiles, setSavedpcEvdncFiles] = React.useState<budgetFilesProps[]>([]); // 저장된 파일

    // 특별회계기금 지출증빙내역서 상세정보 조회
    React.useEffect(() => {
        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/bgt/spc/expnd/select-bgt-spc-evdnc.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                spcAcntgExpndEvdncId: spcAcntgExpndEvdncId  // 지출증빙내역서 ID
            },
            onSuccessFn: (res: any) => {
                if (Util.isNotEmpty(res.result.item)) {
                    setUseDt    (res.result.item.useDt    ); // 사용일자
                    setUsePrpsCn(res.result.item.usePrpsCn); // 사용목적내용
                    setUseCn    (res.result.item.useCn    ); // 사용내용
                    setUseAmt   (res.result.item.useAmt   ); // 사용금액
                    setEtcCn    (res.result.item.etcCn    ); // 기타내용
                    setRmrkCn   (res.result.item.rmrkCn   ); // 비고내용
                    setSavedpcEvdncFiles(res.result.listFile); // 첨부파일
                }
                AppContext.hideSpinner();
            },
        });
    }, [spcAcntgExpndEvdncId]);

    // 특별회계기금 지출증빙내역서 저장
    function doSave() {
        // 필수입력사항 확인
        if (Util.isEmpty(useDt    )) { AppContext.openAlert({ title: "사용일자 확인", msg: "사용일자를 선택하세요.", icon: "warning" }); return; }
        if (Util.isEmpty(useAmt   )) { AppContext.openAlert({ title: "사용금액 확인", msg: "사용금액을 입력하세요.", icon: "warning" }); return; }
        if (Util.isEmpty(usePrpsCn)) { AppContext.openAlert({ title: "사용목적 확인", msg: "사용목적을 입력하세요.", icon: "warning" }); return; }

        // (지출증빙내역서 금액의 합계 + 사용금액)이 집행가능한 금액을 초과할 수 없다.
        // 집행가능금액 - (지출증빙내역서 합계 금액 + 사용 금액)
        if (Number(Util.uncomma(availAmount)) - (Number(sumUseAmt) + Number(Util.uncomma(useAmt))) < 0) {
            AppContext.openAlert({ title: "사용금액 확인", msg: "사용금액[" + Util.comma((Number(sumUseAmt) + Number(Util.uncomma(useAmt))).toString()) + "]이 "
                                + "집행가능금액[" + Util.comma(availAmount) + "]보다 큽니다.", icon: "warning" }); return;
        }

        if (isEvdncUse && Util.isEmpty(spcEvdncFiles) && savedSpcEvdncFiles.length === 0) { AppContext.openAlert({ title: "첨부파일 확인", msg: "지출증빙 첨부파일을 등록하세요", icon: "warning" }); return; }

        AppContext.openAlert({
            title: "확인",
            msg: "지출증빙내역서를 저장하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: "저장",
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/spc/expnd/save-bgt-spc-evdnc.do",
                    methodType: "post",
                    isMultipart: true,
                    paramData: {
                        spcAcntgExpndEvdncId: spcAcntgExpndEvdncId, // 지출증빙내역서 ID
                        spcAcntgExpndDocId  : spcAcntgExpndDocId  , // 지출결의서 문서 ID
                        useDt               : useDt               , // 사용일자 (YYYYMMDD)
                        useAmt              : Util.uncomma(useAmt), // 사용금액
                        usePrpsCn           : usePrpsCn           , // 사용목적
                        useCn               : useCn               , // 내용
                        etcCn               : etcCn               , // 기타 내용
                        rmrkCn              : rmrkCn              , // 비고 내용
                        expndRsdocSeCd      : expndRsdocSeCd      , // 결의서 구분 코드(여입/자료제출)

                        budgetFiles: spcEvdncFiles, // 첨부파일
                        acntgYear  : acntgYear    , // 회계연도
                        fileSeCd   : "B03"        , // 파일구분코드(특별회계기금_지출증빙내역서[B03])
                    },
                    onSuccessFn: (res: any) => {
                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "지출증빙내역서 저장",
                            msg: "지출증빙내역서가 저장되었습니다.",
                            icon: "check"
                        });
                        setSpcEvdncDelYn("N"); // 지출증빙내역서 삭제 여부
                        setCallTime(new Date());
                        setSpcEvdncModalOpen(false);
                    },
                });
            },
        });
    }

    // 삭제
    function doDelete() {
        AppContext.openAlert({
            title: "확인",
            msg: "지출증빙내역서를 삭제하시겠습니까?",
            icon: "question",
            closeText: "취소",
            confirmText: "삭제",
            handleConfirm: () => {
                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/bgt/spc/expnd/delete-bgt-spc-evdnc.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: {
                        spcAcntgExpndEvdncId: spcAcntgExpndEvdncId, // 지출증빙내역서 ID
                        spcAcntgExpndDocId  : spcAcntgExpndDocId  , // 지출결의서 문서 ID
                    },
                    onSuccessFn: (res: any) => {
                        AppContext.hideSpinner();
                        AppContext.openAlert({
                            title: "지출증빙내역서 삭제",
                            msg: "지출증빙내역서가 삭제되었습니다.",
                            icon: "check"
                        });
                        setSpcEvdncDelYn("Y"); // 지출증빙내역서 삭제 여부
                        setCallTime(new Date());
                        setSpcEvdncModalOpen(false);
                    },
                });
            },
        });
    }

    // 초기화
    function doReset() {
        setUseDt    (""); // 사용일자
        setUsePrpsCn(""); // 사용목적내용
        setUseCn    (""); // 사용내용
        setUseAmt   (""); // 사용금액
        setEtcCn    (""); // 기타내용
        setRmrkCn   (""); // 비고내용

        setSpcEvdncFiles    ([]); // 신규 첨부(저장할 파일)
        setSavedpcEvdncFiles([]); // 저장된 파일
    }

    return (
        <div className="pb10 pl10 pr10">
            <div className="dpFlx gap20">
                {/* 사용일자 */}
                <div className="w200">
                    <div className="h30 dpFlx aiE pb5 bdBox">
                        <span className="fm-b fs13 ls06 fc111111">사용일자</span>
                    </div>
                    <DatePickerBox value={useDt}
                        width={200} height={36}
                        fontSize={13} fontFamily="fm-c" color="#222" letterSpacing={-0.5 as any}
                        borderColor="#cfcfcf" borderOnColor="#222" borderRadius={5}
                        bgColor="white" textAlign="left"
                        calendarThemeColor={AppContext.themeColor}
                        onChange={(data: string) => setUseDt(data)}
                    />
                </div>
                <div className="w200">
                    <div className="h30 dpFlx aiE pb5 bdBox">
                        <span className="fm-b fs13 ls06 fc111111">집행 예정 금액</span>
                    </div>
                    <div className="h36 dpFlx aiC bd1 bdrd5 bdcCFCFCF bgcF0F0F0 bsBdb">
                        <NumberTextBox value={sumUseAmt}
                            className="w100p h100p ml10 taR"
                            thousandsGroupStyle="thousand" thousandSeparator=","
                            placeholder="0"
                            fontSize={13} fontFamily="fm-c" letterSpacing={-0.5} color="#222"
                            inputClass={"taR mr10"}
                            disabled={true}
                            // onChange={(v: string) => setUseAmt(v)}
                        />
                    </div>
                </div>
            </div>

            <div className="dpFlx gap20">
                <div className="w200">
                    <div className="h30 dpFlx aiE pb5 bdBox">
                        <span className="fm-b fs13 ls06 fc111111">금액</span>
                    </div>
                    <div className="h36 dpFlx aiC bd1 bdrd5 bdcCFCFCF bgcWhite bsBdb">
                        <NumberTextBox value={useAmt}
                            className="w200 h100p ml10 taR"
                            thousandsGroupStyle="thousand" thousandSeparator=","
                            placeholder="0"
                            fontSize={13} fontFamily="fm-c" letterSpacing={-0.5} color="#222"
                            inputClass={"taR mr10"}
                            onChange={(v: string) => setUseAmt(v)}
                        />
                    </div>
                </div>
                <div className="w200">
                    <div className="h30 dpFlx aiE pb5 bdBox">
                        <span className="fm-b fs13 ls06 fc111111">집행 가능 금액</span>
                    </div>
                    <div className="h36 dpFlx aiC bd1 bdrd5 bdcCFCFCF bgcF0F0F0 bsBdb">
                        <NumberTextBox value={availAmount}
                            className="w100p h100p ml10 taR"
                            thousandsGroupStyle="thousand" thousandSeparator=","
                            placeholder="0"
                            fontSize={13} fontFamily="fm-c" letterSpacing={-0.5} color="#222"
                            inputClass={"taR mr10"}
                            disabled={true}
                            // onChange={(v: string) => setUseAmt(v)}
                        />
                    </div>
                </div>
            </div>

            {/* 사용목적 */}
            <div className="w100p">
                <div className="h30 dpFlx aiE pb5 bdBox">
                    <span className="fm-b fs13 ls06 fc111111">사용목적</span>
                </div>
                <TextBox  value={usePrpsCn}
                    width={420} height={36}
                    fontSize={13} fontFamily="fm-c" color="#222" letterSpacing={-0.5}
                    borderColor="#cfcfcf" borderOnColor="#222" borderRadius={5}
                    style={{ backgroundColor: "#fff", padding: "0 10px" }}
                    placeholder="예: 사무용품 구입"
                    onChangeFunc={(text: string) => setUsePrpsCn(text)}
                />
            </div>

            {/* 사용내용 */}
            <div className="mt10">
                <div className="h30 dpFlx aiE pb5 bdBox">
                    <span className="fm-b fs13 ls06 fc111111">내용</span>
                </div>
                <div className="flxg1 bsBdb">
                    <textarea value={useCn}
                        className="w100p h100 bsBdb p10 bdrd5 bd1 bdcDFDFDF bdrd2 fm-c fs13 fc333333 ls1 lh20 bgcWhite rszN"
                        maxLength={2000}
                        placeholder="내용을 입력해주세요."
                        onChange={(e) => setUseCn(e.target.value)}
                    />
                </div>
            </div>

            {/* 기타내용 */}
            <div className="">
                <div className="h30 dpFlx aiE pb5 bdBox">
                    <span className="fm-b fs13 ls06 fc111111">기타 내용</span>
                </div>
                <div className="flxg1 bsBdb">
                    <textarea value={etcCn}
                        className="w100p h50 bsBdb p10 bdrd5 bd1 bdcDFDFDF bdrd5 fm-c fs13 fc333333 ls1 lh20 bgcWhite rszN"
                        maxLength={2000}
                        placeholder="기타 내용을 입력해주세요."
                        onChange={(e) => setEtcCn(e.target.value)}
                    />
                </div>
            </div>
            {/* 비고 */}
            <div className="mt10">
                <div className="h30 dpFlx aiE pb5 bdBox">
                    <span className="fm-b fs13 ls06 fc111111">비고</span>
                </div>
                <div className="flxg1 bsBdb">
                    <textarea
                        value={rmrkCn}
                        className="w100p h50 bsBdb p10 bdrd5 bd1 bdcDFDFDF bdrd2 fm-c fs13 fc333333 ls1 lh20 bgcWhite rszN"
                        maxLength={2000}
                        placeholder="비고를 입력해주세요."
                        onChange={(e) => setRmrkCn(e.target.value)}
                    />
                </div>
            </div>

            {/* 첨부파일(수입과 동일 컴포넌트 사용) */}
            <BudgetFile
                atrzSttsCd          = "01"    // 결재상태코드(작성중[01] or 작성완료[02]) - 파일 삭제버튼 미표시 처리용
                fileSeCd            = "B03"   // 파일구분코드(특별회계기금_수입결의서[B01])
                savedBudgetFiles    = {savedSpcEvdncFiles}
                setSavedBudgetFiles = {setSavedpcEvdncFiles}
                budgetFiles         = {spcEvdncFiles}
                setBudgetFiles      = {setSpcEvdncFiles}
                width               = {420}
                height              = {100}
            />

            <div className="dpFlx aiC jcC mt25 gap10">
                <Button buttonName="닫기"
                    width={80} height={40}
                    fontFamily="fm-b" fontSize={16} letterSpacing={-1} 
                    variant="styCloseBtn" borderRadius={6}
                    buttonClass="dpFlx aiC jcC"
                    onClickFunc={() => {
                        setSpcAcntgExpndEvdncId("");
                        setSpcEvdncModalOpen(false);
                    }}
                />
                <Button buttonName="초기화"
                    width={80} height={40}
                    fontFamily="fm-b" fontSize={16} letterSpacing={-1} 
                    variant="styInitBtn" borderRadius={6}
                    buttonClass="dpFlx aiC jcC"
                    onClickFunc={() => doReset()}
                />
                {spcAcntgExpndEvdncId && (
                    <Button buttonName="삭제"
                        width={80} height={40}
                        fontFamily="fm-b" fontSize={16} letterSpacing={-1} 
                        variant="styDeleteBtn" borderRadius={6}
                        buttonClass="dpFlx aiC jcC"
                        onClickFunc={() => doDelete()}
                    />
                )}

                <Button buttonName="저장"
                    width={80} height={40}
                    fontFamily="fm-b" fontSize={16} letterSpacing={-1} 
                    variant="styCompleteBtn" borderRadius={6}
                    buttonClass="dpFlx aiC jcC"
                    onClickFunc={() => doSave()}
                />
            </div>
        </div>
    )
}

export default BgtSpcEvdncDialog;